import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ForceGraph2D from 'react-force-graph-2d';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Skeleton from './common/Skeleton';

const BASE_URL = config.BASE_URL;

const SEVERITY_COLORS = {
  CRITICAL: '#dc2626',
  HIGH:     '#ea580c',
  MEDIUM:   '#d97706',
  LOW:      '#16a34a',
  '':       '#6b7280',
};

function SeverityBadge({ severity }) {
  const sev = (severity || '').toLowerCase() || 'none';
  const cls = ['critical','high','medium','low'].includes(sev) ? sev : 'none';
  return <span className={`ce-sev ce-sev--${cls}`}>{severity || 'None'}</span>;
}

export default function DependencyGraph({ repoName, sbomId }) {
  const { t } = useTranslation();
  const fgRef = useRef();
  const containerRef = useRef();

  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dimensions, setDimensions] = useState({ width: 800, height: 560 });
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());

  // Observe container width
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({ width: entry.contentRect.width, height: 560 });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const load = useCallback(async () => {
    if (!repoName || !sbomId) return;
    setLoading(true); setError(null);
    const sbomFile = sbomId.endsWith('-sbom.json') ? sbomId : `${sbomId}-sbom.json`;
    try {
      const res = await authFetch(`${BASE_URL}/sbom/${repoName}/${sbomFile}/graph`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      // Build force-graph format
      const nodeMap = {};
      data.nodes.forEach(n => { nodeMap[n.id] = n; });
      setGraphData({
        raw: data,
        nodes: data.nodes.map(n => ({ ...n, __neighbors: [], __links: [] })),
        links: data.edges.map(e => ({ source: e.source, target: e.target })),
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [repoName, sbomId]);

  useEffect(() => { load(); }, [load]);

  // Filtered graph
  const filteredGraph = React.useMemo(() => {
    if (!graphData) return null;
    let nodes = graphData.nodes;
    if (filterSeverity !== 'ALL') {
      nodes = nodes.filter(n => n.severity === filterSeverity || (filterSeverity === 'NONE' && !n.severity));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      nodes = nodes.filter(n => n.label.toLowerCase().includes(q) || (n.purl || '').toLowerCase().includes(q));
    }
    const nodeIds = new Set(nodes.map(n => n.id));
    const links = graphData.links.filter(l => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      return nodeIds.has(src) && nodeIds.has(tgt);
    });
    return { nodes, links };
  }, [graphData, filterSeverity, search]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    // Highlight neighbors
    const neighbors = new Set();
    const links = new Set();
    filteredGraph?.links.forEach(l => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      if (src === node.id || tgt === node.id) {
        neighbors.add(src); neighbors.add(tgt);
        links.add(l);
      }
    });
    setHighlightNodes(neighbors);
    setHighlightLinks(links);
  }, [filteredGraph]);

  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
  }, []);

  // Stats
  const stats = React.useMemo(() => {
    if (!graphData?.raw) return {};
    const r = graphData.raw;
    return {
      total: r.totalNodes,
      shown: filteredGraph?.nodes.length || 0,
      edges: filteredGraph?.links.length || 0,
      critical: graphData.nodes.filter(n => n.severity === 'CRITICAL').length,
      high: graphData.nodes.filter(n => n.severity === 'HIGH').length,
      withCves: graphData.nodes.filter(n => n.vulnCount > 0).length,
      format: r.format,
      hasDeps: r.hasDeps,
      truncated: r.truncated,
    };
  }, [graphData, filteredGraph]);

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Card />
      <div className="small text-secondary mt-3">
        {t('common.loading', 'Laden...')} — {t('graph.loadingNote', 'Grote SBOMs kunnen even duren')}
      </div>
    </div>
  );
  if (error) return <div className="alert alert-danger">{t('graph.loadError', 'Graaf laden mislukt')}: {error}</div>;
  if (!graphData) return null;

  return (
    <div>
      {/* Stats bar */}
      <div className="ce-kpi-row" style={{ marginBottom: '1rem' }}>
        {[
          { icon: '📦', label: t('graph.packages', 'Pakketten'), value: stats.total, color: '#6366f1' },
          { icon: '🔗', label: t('graph.deps', 'Afhankelijkheden'), value: stats.edges, color: '#6366f1' },
          { icon: '🔴', label: t('graph.critical', 'Kritiek'), value: stats.critical, color: stats.critical > 0 ? '#dc2626' : '#6b7280' },
          { icon: '🟠', label: t('graph.high', 'Hoog'), value: stats.high, color: stats.high > 0 ? '#ea580c' : '#6b7280' },
          { icon: '🛡️', label: t('graph.withCves', 'Met CVEs'), value: stats.withCves, color: stats.withCves > 0 ? '#ea580c' : '#16a34a' },
        ].map(s => (
          <div key={s.label} className="ce-kpi" style={{ '--kpi-color': s.color }}>
            <div className="ce-kpi-value">{s.icon} {s.value}</div>
            <div className="ce-kpi-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {!stats.hasDeps && (
        <div className="alert alert-info d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.875rem' }}>
          <span>ℹ️</span>
          <span>{t('graph.noDepsInfo', 'Dit SBOM bevat geen afhankelijkheidsrelaties (SPDX zonder DEPENDS_ON). Pakketten worden als losse nodes weergegeven. Upload een CycloneDX SBOM voor een volledige dependency graph.')}</span>
        </div>
      )}
      {stats.truncated && (
        <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.875rem' }}>
          <span>⚠️</span>
          <span>{t('graph.truncatedInfo', `Graaf is beperkt tot 500 nodes (van de ${stats.total} pakketten). Pakketten met CVEs krijgen prioriteit.`)}</span>
        </div>
      )}

      {/* Controls */}
      <div className="ce-filter-bar" style={{ marginBottom: '0.75rem' }}>
        <input className="ce-search-input" style={{ maxWidth: 220 }} placeholder={t('graph.search', 'Pakket zoeken…')}
          value={search} onChange={e => setSearch(e.target.value)} />
        {search && <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => setSearch('')}>✕</button>}
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'].map(sev => (
            <button key={sev} onClick={() => setFilterSeverity(sev)}
              className={`ce-btn ce-btn--sm ${filterSeverity === sev ? 'ce-btn--primary' : 'ce-btn--secondary'}`}>
              {sev === 'ALL' ? 'Alle' : sev === 'NONE' ? 'Geen CVE' : sev}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => fgRef.current?.zoomToFit(400)}>
            ⊞ {t('graph.fit', 'Passend')}
          </button>
          <span className="ce-filter-count">{stats.shown} nodes · {stats.edges} edges · {stats.format?.toUpperCase()}</span>
        </div>
      </div>

      {/* Graph + detail panel */}
      <div className="d-flex gap-3 align-items-start">
        <div ref={containerRef} className="ce-card" style={{ flex: 1, overflow: 'hidden' }}>
          {filteredGraph && (
            <ForceGraph2D
              ref={fgRef}
              graphData={filteredGraph}
              width={dimensions.width}
              height={dimensions.height}
              nodeId="id"
              nodeLabel={n => `${n.label}@${n.version || '?'}${n.vulnCount > 0 ? ` · ${n.vulnCount} CVE(s) [${n.severity}]` : ''}`}
              nodeColor={n => {
                if (highlightNodes.size > 0 && !highlightNodes.has(n.id)) return '#cccccc44';
                return n.color || '#6b7280';
              }}
              nodeVal={n => {
                const base = n.isDirect ? 8 : 4;
                if (n.severity === 'CRITICAL') return base + 8;
                if (n.severity === 'HIGH') return base + 4;
                if (n.severity === 'MEDIUM') return base + 2;
                return base;
              }}
              nodeCanvasObjectMode={() => 'after'}
              nodeCanvasObject={(node, ctx, globalScale) => {
                if (globalScale < 2) return;
                const label = node.label.length > 16 ? node.label.slice(0, 14) + '…' : node.label;
                ctx.font = `${Math.min(12, 10 / globalScale * 2)}px sans-serif`;
                ctx.fillStyle = 'var(--bs-body-color, #333)';
                ctx.textAlign = 'center';
                ctx.fillText(label, node.x, node.y + 10);
              }}
              linkColor={l => {
                if (highlightLinks.size > 0 && !highlightLinks.has(l)) return '#cccccc22';
                return 'var(--bs-border-color, #ccc)';
              }}
              linkWidth={l => highlightLinks.has(l) ? 2 : 1}
              linkDirectionalArrowLength={stats.hasDeps ? 4 : 0}
              linkDirectionalArrowRelPos={1}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              cooldownTicks={100}
              onEngineStop={() => {
                if (filteredGraph?.nodes.length < 100) fgRef.current?.zoomToFit(400);
              }}
            />
          )}
        </div>

        {/* Node detail panel */}
        {selectedNode && (
          <div className="ce-card" style={{ width: '260px', flexShrink: 0 }}>
            <div className="ce-card-header">
              <span style={{ fontWeight: 700, fontSize: '0.875rem', wordBreak: 'break-word', flex: 1 }}>{selectedNode.label}</span>
              <button className="ce-icon-btn" onClick={() => setSelectedNode(null)}>✕</button>
            </div>
            <div className="ce-card-body" style={{ padding: '0.75rem' }}>
              <div className="ce-info-grid">
                <span className="ce-info-label">Versie</span><span className="ce-info-value">{selectedNode.version || '—'}</span>
                <span className="ce-info-label">Ecosystem</span><span className="ce-info-value">{selectedNode.group || '—'}</span>
                <span className="ce-info-label">Direct dep</span><span className="ce-info-value">{selectedNode.isDirect ? '✅ Ja' : '↩️ Transitief'}</span>
                <span className="ce-info-label">Severity</span><span className="ce-info-value"><SeverityBadge severity={selectedNode.severity} /></span>
                <span className="ce-info-label">CVEs</span><span className="ce-info-value">{selectedNode.vulnCount || 0}</span>
              </div>
              {selectedNode.purl && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)', marginBottom: '0.2rem' }}>PURL</div>
                  <code className="ce-mono" style={{ wordBreak: 'break-all', fontSize: '0.7rem' }}>{selectedNode.purl}</code>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        {Object.entries(SEVERITY_COLORS).filter(([k]) => k !== '').map(([sev, color]) => (
          <span key={sev} className="ce-tag" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {sev || 'NONE'}
          </span>
        ))}
        <span className="ce-tag">· Klik op node voor details · Scroll om te zoomen · Sleep om te pannen</span>
      </div>
    </div>
  );
}
